import { createSelector } from 'reselect';

// Base selectors
const selectAssessmentsState = (state) => state.assessments;
const selectAppState = (state) => state.app;

// Select current assessment UUID from app state
export const selectCurrentAssessmentUuid = createSelector(
  [selectAppState],
  (app) => app?.currentAssessmentUuid || null
);

// Select all assessments
export const selectAllAssessments = createSelector(
  [selectAssessmentsState],
  (assessments) => assessments?.byId || {}
);

// Select assessment by ID (parameterized selector)
export const selectAssessmentById = (assessmentUuid) =>
  createSelector(
    [selectAllAssessments],
    (assessments) => assessments[assessmentUuid] || null
  );

// Select current assessment
export const selectCurrentAssessment = createSelector(
  [selectAllAssessments, selectCurrentAssessmentUuid],
  (assessments, currentUuid) => {
    if (!currentUuid) return null;
    return assessments[currentUuid] || null;
  }
);

// Select assessment info
export const selectAssessmentInfo = createSelector(
  [selectCurrentAssessment],
  (assessment) => assessment?.info || null
);

// Select assessment info by ID
export const selectAssessmentInfoById = (assessmentUuid) =>
  createSelector(
    [selectAssessmentById(assessmentUuid)],
    (assessment) => assessment?.info || null
  );

// Select assessment context
export const selectAssessmentContext = createSelector(
  [selectCurrentAssessment],
  (assessment) => assessment?.context || {}
);

// Select assessment context by ID
export const selectAssessmentContextById = (assessmentUuid) =>
  createSelector(
    [selectAssessmentById(assessmentUuid)],
    (assessment) => assessment?.context || {}
  );

// Select specific context section
export const selectContextSection = (section) =>
  createSelector(
    [selectAssessmentContext],
    (context) => context[section] || {}
  );

// Select specific context section by ID
export const selectContextSectionById = (assessmentUuid, section) =>
  createSelector(
    [selectAssessmentContextById(assessmentUuid)],
    (context) => context[section] || {}
  );

// Select model risks (was: riskFormData)
export const selectModelRisks = createSelector(
  [selectCurrentAssessment],
  (assessment) => assessment?.modelRisks || {}
);

// Select model risks by ID
export const selectModelRisksById = (assessmentUuid) =>
  createSelector(
    [selectAssessmentById(assessmentUuid)],
    (assessment) => assessment?.modelRisks || {}
  );

// Select model risks for specific risk area
export const selectModelRisksByArea = (riskArea) =>
  createSelector(
    [selectModelRisks],
    (modelRisks) => modelRisks[riskArea] || {}
  );

// Select model risks for specific risk area by ID
export const selectModelRisksByAreaById = (assessmentUuid, riskArea) =>
  createSelector(
    [selectModelRisksById(assessmentUuid)],
    (modelRisks) => modelRisks[riskArea] || {}
  );

// Legacy aliases for backward compatibility
export const selectRiskFormData = selectModelRisks;
export const selectRiskFormDataById = selectModelRisksById;
export const selectRiskAreaFormData = selectModelRisksByArea;
export const selectRiskAreaFormDataById = selectModelRisksByAreaById;

// Select saved risks
export const selectSavedRisks = createSelector(
  [selectCurrentAssessment],
  (assessment) => assessment?.savedRisks || []
);

// Select saved risks by ID
export const selectSavedRisksById = (assessmentUuid) =>
  createSelector(
    [selectAssessmentById(assessmentUuid)],
    (assessment) => assessment?.savedRisks || []
  );

// Select specific saved risk
export const selectSavedRiskById = (riskId) =>
  createSelector(
    [selectSavedRisks],
    (risks) => risks.find((r) => r.risk_id === riskId) || null
  );

// Select security data
export const selectSecurity = createSelector(
  [selectCurrentAssessment],
  (assessment) => assessment?.security || {}
);

// Select security data by ID
export const selectSecurityById = (assessmentUuid) =>
  createSelector(
    [selectAssessmentById(assessmentUuid)],
    (assessment) => assessment?.security || {}
  );

// Select security scenarios
export const selectSecurityScenarios = createSelector(
  [selectSecurity],
  (security) => security?.scenarios || {}
);

// Select security scenarios by ID
export const selectSecurityScenariosById = (assessmentUuid) =>
  createSelector(
    [selectSecurityById(assessmentUuid)],
    (security) => security?.scenarios || {}
  );

// Select security controls
export const selectSecurityControls = createSelector(
  [selectSecurity],
  (security) => security?.securityControls || {}
);

// Select security controls by ID
export const selectSecurityControlsById = (assessmentUuid) =>
  createSelector(
    [selectSecurityById(assessmentUuid)],
    (security) => security?.securityControls || {}
  );

// Select residual risk
export const selectResidualRisk = createSelector(
  [selectCurrentAssessment],
  (assessment) => assessment?.residualRisk || {}
);

// Select residual risk by ID
export const selectResidualRiskById = (assessmentUuid) =>
  createSelector(
    [selectAssessmentById(assessmentUuid)],
    (assessment) => assessment?.residualRisk || {}
  );

// Select residual risk for specific risk
export const selectResidualRiskForRiskId = (riskId) =>
  createSelector(
    [selectResidualRisk],
    (residualRisk) => residualRisk[riskId] || null
  );

// Select residual risk for specific risk by assessment ID
export const selectResidualRiskForRiskIdByAssessment = (assessmentUuid, riskId) =>
  createSelector(
    [selectResidualRiskById(assessmentUuid)],
    (residualRisk) => residualRisk[riskId] || null
  );

// Select assessment loading state
export const selectAssessmentLoading = createSelector(
  [selectAssessmentsState, selectCurrentAssessmentUuid],
  (assessments, currentUuid) => {
    if (!currentUuid) return false;
    return assessments?.loading?.[currentUuid] || false;
  }
);

// Select assessment loading state by ID
export const selectAssessmentLoadingById = (assessmentUuid) =>
  createSelector(
    [selectAssessmentsState],
    (assessments) => assessments?.loading?.[assessmentUuid] || false
  );

// Select assessment error
export const selectAssessmentError = createSelector(
  [selectAssessmentsState, selectCurrentAssessmentUuid],
  (assessments, currentUuid) => {
    if (!currentUuid) return null;
    return assessments?.error?.[currentUuid] || null;
  }
);

// Select assessment error by ID
export const selectAssessmentErrorById = (assessmentUuid) =>
  createSelector(
    [selectAssessmentsState],
    (assessments) => assessments?.error?.[assessmentUuid] || null
  );

// Select model UUID from current assessment
export const selectAssessmentModelUuid = createSelector(
  [selectCurrentAssessment],
  (assessment) => assessment?.model_uuid || null
);

// Select model UUID from assessment by ID
export const selectAssessmentModelUuidById = (assessmentUuid) =>
  createSelector(
    [selectAssessmentById(assessmentUuid)],
    (assessment) => assessment?.model_uuid || null
  );

// ============================================================================
// LEGACY SELECTORS - Updated to use new assessment-centric structure
// ============================================================================

// Legacy base selectors - now using assessment-centric structure
const selectModelRisksOld = (state, assessmentUuid) => state.risks?.byAssessment?.[assessmentUuid]?.modelRisks || {};
const selectControlsStateOld = state => state.controls?.savedControls || {};
const selectResidualByAssessmentOld = (state, assessmentUuid) => state.residualRisk?.byAssessment?.[assessmentUuid] || {};
const selectSecurityDataOld = (state, assessmentUuid) => state.securityReportData?.dataByAssessment?.[assessmentUuid] || {};
const selectContextDataOld = (state, assessmentUuid) => state.context?.data?.[assessmentUuid] || {};

// Legacy export aliases
export const selectRiskFormDataLegacy = selectModelRisksOld;
export const selectControlsStateLegacy = selectControlsStateOld;
export const selectResidualByModelLegacy = selectResidualByAssessmentOld;
export const selectSecurityDataLegacy = selectSecurityDataOld;
export const selectContextDataLegacy = selectContextDataOld;

// Legacy complex selectors (updated to use areaRisks instead of sectionRisks)
export const selectRiskEntries = createSelector(
  [ selectModelRisksOld, selectControlsStateOld, selectResidualByAssessmentOld ],
  (modelRisks, controlsState, residualByAssessment) =>
    Object.entries(modelRisks).flatMap(
      ([section, catObj]) =>
        Object.entries(catObj.areaRisks || {}).flatMap(
          ([category, risks]) =>
            (risks || []).map(risk => ({
              riskId:   risk.riskId,
              title:    risk.riskName,
              section,
              category,
              priority: risk.priority,
              residual:
                residualByAssessment[risk.riskId]?.overridden?.residual_rating ||
                residualByAssessment[risk.riskId]?.residual_rating ||
                'N/A',
              controls: controlsState[risk.riskId] || []
            }))
        )
    )
);

export const selectAllControls = createSelector(
  [ selectControlsStateOld ],
  controlsState => {
    const all = {};
    Object.entries(controlsState).forEach(([rid, list]) => {
      all[rid] = Array.isArray(list) ? list : [];
    });
    return all;
  }
);

export const selectContextNugget = createSelector(
  [ selectContextDataOld ],
  ctx => ({
    useCase:         ctx.useCase?.narrative || '',
    businessContext: ctx.businessContext || '',
    modelContext:    ctx.modelContext || ''
  })
);

export const selectNarratives = createSelector(
  [ selectContextDataOld ],
  ctx =>
    Object.entries(ctx).reduce((acc, [section, val]) => {
      acc[section] = val.narrative || `No narrative for ${section}`;
      return acc;
    }, {})
);

export const makeSelectNarrative = section => createSelector(
  [selectContextDataOld],
  ctx => ctx[section]?.narrative || ''
);

// ============================================================================
// NEW ASSESSMENT-CENTRIC SELECTORS
// ============================================================================

/**
 * Select model risks for a specific assessment from the risks slice
 * Usage: selectModelRisksByAssessment(assessmentUuid)(state)
 */
export const selectModelRisksByAssessment = (assessmentUuid) =>
  createSelector(
    [(state) => state.risks?.byAssessment?.[assessmentUuid]],
    (assessment) => assessment?.modelRisks || {}
  );

/**
 * Select full assessment data from the risks slice
 * Usage: selectAssessmentFromRisks(assessmentUuid)(state)
 */
export const selectAssessmentFromRisks = (assessmentUuid) =>
  createSelector(
    [(state) => state.risks?.byAssessment?.[assessmentUuid]],
    (assessment) => assessment || null
  );

/**
 * Select area risks for a specific area within an assessment
 * Usage: selectAreaRisks(assessmentUuid, areaName)(state)
 */
export const selectAreaRisks = (assessmentUuid, areaName) =>
  createSelector(
    [selectModelRisksByAssessment(assessmentUuid)],
    (modelRisks) => modelRisks[areaName]?.areaRisks || {}
  );

/**
 * Select risk sources for a specific area within an assessment
 * Usage: selectRiskSources(assessmentUuid, areaName)(state)
 */
export const selectRiskSources = (assessmentUuid, areaName) =>
  createSelector(
    [selectModelRisksByAssessment(assessmentUuid)],
    (modelRisks) => modelRisks[areaName]?.riskSources || []
  );

/**
 * Select all byAssessment from risks slice
 */
export const selectAllByAssessment = createSelector(
  [(state) => state.risks],
  (risks) => risks?.byAssessment || {}
);

/**
 * Select current assessment UUID from risks slice
 */
export const selectCurrentAssessmentUuidFromRisks = createSelector(
  [(state) => state.risks],
  (risks) => risks?.currentAssessmentUuid || null
);

// ============================================================================
// BRIDGE SELECTORS - Simplified for new assessment-centric structure
// These are kept for backward compatibility during component migration
// Note: modelId parameter is kept for backward compatibility but ignored
// ============================================================================

/**
 * Bridge selector for model risks (was: riskFormData)
 * RTK-first: reads from riskItems (RTK autopilot) first, falls back to risks (legacy)
 * @param assessmentUuid - The assessment UUID (primary key)
 * @param _modelId - DEPRECATED: kept for backward compatibility, ignored
 */
export const selectRiskFormDataBridge = (assessmentUuid, _modelId) =>
  createSelector(
    [
      (state) => state.riskItems?.byAssessment?.[assessmentUuid],
      (state) => state.risks?.byAssessment?.[assessmentUuid]?.modelRisks,
    ],
    (rtkAssessment, legacyModelRisks) => {
      // RTK slice (riskItems) has structure: { modelRisks: { [areaName]: { areaRisks: {} } } }
      // The modelRisks wrapper is used for compatibility with legacy selectors
      if (rtkAssessment && rtkAssessment.modelRisks && Object.keys(rtkAssessment.modelRisks).length > 0) {
        return rtkAssessment.modelRisks;
      }
      return legacyModelRisks || {};
    }
  );

/**
 * Bridge selector for full risks state
 * RTK-first: reads from riskItems (RTK autopilot) first, falls back to risks (legacy)
 * Returns structure compatible with components expecting assessment data
 * @param assessmentUuid - The assessment UUID (primary key)
 * @param _modelId - DEPRECATED: kept for backward compatibility, ignored
 */
export const selectRisksStateBridge = (assessmentUuid, _modelId) =>
  createSelector(
    [
      (state) => state.riskItems?.byAssessment?.[assessmentUuid],
      (state) => state.risks?.byAssessment?.[assessmentUuid],
      (state) => state.assessments?.byId?.[assessmentUuid],
    ],
    (rtkAssessment, legacyAssessment, assessmentInfo) => {
      // RTK slice (riskItems) has structure: { modelRisks: { [areaName]: { areaRisks: {} } } }
      // The modelRisks wrapper is used for compatibility with legacy selectors
      if (rtkAssessment && rtkAssessment.modelRisks && Object.keys(rtkAssessment.modelRisks).length > 0) {
        const modelRisks = rtkAssessment.modelRisks;
        return {
          riskFormData: modelRisks,
          modelRisks: modelRisks,
          savedRisks: [], // RTK slice doesn't use this format
          modelName: assessmentInfo?.model_name || assessmentInfo?.info?.assessment_name || '',
          modelType: assessmentInfo?.model_type || '',
          modelStage: assessmentInfo?.model_stage || '',
          assessmentUuid: assessmentUuid,
          modelId: assessmentInfo?.model_uuid || '',
        };
      }
      // Fall back to legacy
      if (!legacyAssessment) return {};
      return {
        riskFormData: legacyAssessment.modelRisks || {},
        modelRisks: legacyAssessment.modelRisks || {},
        savedRisks: legacyAssessment.savedRisks || [],
        modelName: legacyAssessment.modelName || legacyAssessment.assessmentName,
        modelType: legacyAssessment.modelType,
        modelStage: legacyAssessment.modelStage,
        assessmentUuid: assessmentUuid,
        modelId: legacyAssessment.modelId
      };
    }
  );

/**
 * Bridge selector for context data
 * Now reads from context slice keyed by assessmentUuid
 * @param assessmentUuid - The assessment UUID (primary key)
 * @param _modelId - DEPRECATED: kept for backward compatibility, ignored
 */
export const selectContextBridge = (assessmentUuid, _modelId) =>
  createSelector(
    [(state) => state.context?.data?.[assessmentUuid]],
    (context) => context || {}
  );

/**
 * Bridge selector for security data
 * Security reducer currently keys by modelId, not assessmentUuid
 * Try modelId first (actual key used), then assessmentUuid for future compatibility
 * @param assessmentUuid - The assessment UUID
 * @param modelId - The model UUID (actual key used by security reducer)
 */
export const selectSecurityBridge = (assessmentUuid, modelId) =>
  createSelector(
    [
      (state) => state.security?.models?.[modelId],
      (state) => state.security?.models?.[assessmentUuid],
    ],
    (securityByModelId, securityByAssessmentUuid) => {
      // Security reducer uses modelId as key, so try that first
      if (securityByModelId && Object.keys(securityByModelId).length > 0) {
        return securityByModelId;
      }
      // Fall back to assessmentUuid for future compatibility
      return securityByAssessmentUuid || {};
    }
  );

/**
 * Bridge selector for residual risk data
 * RTK-first: reads from residualRiskRTK.byAssessment, falls back to legacy residualRisk.byModel
 * @param assessmentUuid - The assessment UUID (primary key)
 * @param _modelId - DEPRECATED: kept for backward compatibility, ignored
 */
export const selectResidualRiskBridge = (assessmentUuid, _modelId) =>
  createSelector(
    [
      (state) => state.residualRiskRTK?.byAssessment?.[assessmentUuid],
      (state) => state.residualRisk?.byModel?.[assessmentUuid],
    ],
    (rtkResidualRisk, legacyResidualRisk) => {
      // RTK-first: prefer RTK slice data if available
      const rtkData = rtkResidualRisk || {};
      const legacyData = legacyResidualRisk || {};
      // Merge: RTK data takes precedence
      return { ...legacyData, ...rtkData };
    }
  );

/**
 * Bridge selector for saved controls
 * Filters savedControls by assessmentUuid - each control has assessmentUuid from the backend
 * @param assessmentUuid - The assessment UUID to filter controls by
 * @param _modelId - DEPRECATED: kept for backward compatibility, ignored
 */
export const selectSavedControlsBridge = (assessmentUuid, _modelId) =>
  createSelector(
    [(state) => state.controls?.savedControls],
    (savedControls) => {
      if (!savedControls || !assessmentUuid) return {};

      // Filter controls by assessmentUuid - each control has this field from backend
      const filtered = {};
      Object.entries(savedControls).forEach(([riskId, controls]) => {
        const matching = controls.filter(c => c.assessmentUuid === assessmentUuid);
        if (matching.length > 0) {
          filtered[riskId] = matching;
        }
      });
      return filtered;
    }
  );

/**
 * Bridge selector for control evaluations
 * Evaluations are currently global (not per-assessment)
 * @param _assessmentUuid - DEPRECATED: kept for backward compatibility, ignored
 * @param _modelId - DEPRECATED: kept for backward compatibility, ignored
 */
export const selectControlEvaluationsBridge = (_assessmentUuid, _modelId) =>
  createSelector(
    [(state) => state.controls?.savedControlEvaluations],
    (savedControlEvaluations) => savedControlEvaluations || {}
  );

/**
 * Selector for model name - uses RTK models slice as single source of truth
 *
 * @param assessmentUuid - The assessment UUID (for future use, currently unused)
 * @param modelId - The model UUID (used to look up in models slice)
 */
export const selectModelNameBridge = (_assessmentUuid, modelId) =>
  createSelector(
    [(state) => state.models?.byId?.[modelId]],
    (model) => {
      if (!model) {
        console.warn(`[selectModelNameBridge] Model not found in state.models.byId for modelId: ${modelId}`);
        return '';
      }
      return model.model_name || '';
    }
  );

// ============================================================================
// EXPLORER-SPECIFIC SELECTORS
// Re-exported from risksSlice for convenience, plus additional Explorer selectors
// ============================================================================

// Re-export Explorer selectors from risksSlice for convenience
export {
  selectAssessmentType,
  selectExplorerRisks,
  selectExplorerAreaRisks,
  selectExplorerAreaStatus,
  selectExplorerAreaAnalysis,
  selectAllExplorerRisks,
  selectSavedExplorerRisks,
  selectExplorerProgress,
  selectInheritedBuilderRisks,
  selectExplorerRiskById,
  selectExplorerRiskStatusCounts,
  EXPLORER_RISK_AREAS
} from '../slices/risksSlice';

/**
 * Select Explorer risks bridge - combines RTK and legacy sources
 * Uses the same pattern as selectRiskFormDataBridge but for Explorer risks
 * @param assessmentUuid - The assessment UUID
 */
export const selectExplorerRisksBridge = (assessmentUuid) =>
  createSelector(
    [(state) => state.riskItems?.byAssessment?.[assessmentUuid]],
    (assessment) => assessment?.explorerRisks || {}
  );

/**
 * Select inherited risks from parent Builder assessment
 * @param parentAssessmentUuid - The parent Builder assessment UUID
 */
export const selectInheritedRisksBridge = (parentAssessmentUuid) =>
  createSelector(
    [(state) => state.riskItems?.byAssessment?.[parentAssessmentUuid]],
    (parentAssessment) => {
      if (!parentAssessment?.modelRisks) return [];

      const allRisks = [];
      Object.entries(parentAssessment.modelRisks).forEach(([sectionName, sectionData]) => {
        const areaRisks = sectionData?.areaRisks || {};
        Object.entries(areaRisks).forEach(([category, risks]) => {
          allRisks.push(
            ...risks.map((r) => ({
              ...r,
              sectionName,
              category,
            }))
          );
        });
      });

      return allRisks.filter((r) => r.status === 'Saved');
    }
  );

/**
 * Select Explorer area with full details including analysis and risks
 * @param assessmentUuid - The assessment UUID
 * @param areaKey - The Explorer area key (e.g., 'commercialRisk')
 */
export const selectExplorerAreaDetails = (assessmentUuid, areaKey) =>
  createSelector(
    [(state) => state.riskItems?.byAssessment?.[assessmentUuid]?.explorerRisks?.[areaKey]],
    (areaData) => ({
      areaRisks: areaData?.areaRisks || {},
      status: areaData?.areaStatus || 'pending',
      analysis: areaData?.areaAnalysis || null,
      riskCount: Object.values(areaData?.areaRisks || {}).reduce(
        (sum, risks) => sum + (Array.isArray(risks) ? risks.length : 0),
        0
      )
    })
  );

/**
 * Select all Explorer areas with their completion status
 * @param assessmentUuid - The assessment UUID
 */
export const selectExplorerAreasOverview = (assessmentUuid) =>
  createSelector(
    [(state) => state.riskItems?.byAssessment?.[assessmentUuid]?.explorerRisks],
    (explorerRisks) => {
      if (!explorerRisks) return [];

      return Object.entries(explorerRisks).map(([areaKey, areaData]) => ({
        areaKey,
        status: areaData?.areaStatus || 'pending',
        riskCount: Object.values(areaData?.areaRisks || {}).reduce(
          (sum, risks) => sum + (Array.isArray(risks) ? risks.length : 0),
          0
        ),
        hasAnalysis: !!areaData?.areaAnalysis
      }));
    }
  );

/**
 * Select whether an assessment is an Explorer assessment
 * @param assessmentUuid - The assessment UUID
 */
export const selectIsExplorerAssessment = (assessmentUuid) =>
  createSelector(
    [(state) => state.riskItems?.byAssessment?.[assessmentUuid]?.assessmentType],
    (assessmentType) => assessmentType === 'explorer'
  );
